/**
 * `/docs/api` — API reference redirect surface.
 *
 * The live Scalar reference is already hosted by `apps/api` at
 * `${PUBLIC_API_BASE_URL}/v1/docs` (see `apps/api/src/app.ts`) — no
 * need to embed a second copy in the dashboard bundle. We render a
 * small landing card with:
 *   - a primary "Open the API reference" link (opens in a new tab),
 *   - a direct link to the raw OpenAPI JSON for codegen, and
 *   - a short note on auth (Bearer JWT from the dashboard session,
 *     or `sk_live_…` keys minted under Settings → API keys).
 *
 * We open in a new tab rather than iframing because CSP (essentials
 * §5) would have to allow-list the API origin inside `frame-src` for
 * an iframe — cleaner to just link out.
 */
import { CodeSnippet } from '@/components/primitives/CodeSnippet';
import { Island } from '@/components/shell/Island';
import { Button } from '@/components/ui/button';
import { ArrowSquareOut, BookOpen, Code, Key } from '@phosphor-icons/react';
import { Link, createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';

export const Route = createFileRoute('/_app/docs/api')({
  component: ApiDocs,
});

function resolveBase(): string {
  return (import.meta.env.PUBLIC_API_BASE_URL ?? 'https://api.clickweave.co').replace(/\/+$/, '');
}

function ApiDocs(): ReactElement {
  const base = resolveBase();
  const docsUrl = `${base}/v1/docs`;
  const specUrl = `${base}/v1/openapi.json`;

  return (
    <Island breadcrumb={[{ label: 'Docs', href: '/docs' }, { label: 'API' }]}>
      <div className="mx-auto max-w-3xl space-y-6">
        <header className="space-y-2">
          <h1 className="text-2xl font-semibold text-[var(--color-ink)]">API reference</h1>
          <p className="text-sm text-[var(--color-mute)]">
            Full endpoint reference lives on{' '}
            <span className="font-mono text-xs">api.clickweave.co</span> — powered by the same
            OpenAPI document SDKs are generated from. Open it in a new tab so you can keep the
            dashboard open alongside.
          </p>
        </header>

        <section className="flex flex-col gap-3 rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <BookOpen
              aria-hidden="true"
              size={24}
              weight="duotone"
              className="text-[var(--color-accent-brand)]"
            />
            <div>
              <h2 className="text-base font-semibold text-[var(--color-ink)]">
                Interactive API reference
              </h2>
              <p className="text-xs text-[var(--color-mute)]">
                Every public endpoint with request shape, response, and try-it buttons.
              </p>
              <p className="mt-1 font-mono text-xs text-[var(--color-mute)]">{docsUrl}</p>
            </div>
          </div>
          {/* biome-ignore lint/a11y/useAnchorContent: content lives in the Button children which Base UI merges into the rendered <a> */}
          <Button render={<a href={docsUrl} target="_blank" rel="noreferrer noopener" />}>
            <ArrowSquareOut aria-hidden="true" />
            Open reference
          </Button>
        </section>

        <section className="space-y-3 rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-5">
          <div className="flex items-start gap-3">
            <Code
              aria-hidden="true"
              size={24}
              weight="duotone"
              className="text-[var(--color-accent-brand)]"
            />
            <div className="flex-1">
              <h2 className="text-base font-semibold text-[var(--color-ink)]">
                Generate a client from the OpenAPI spec
              </h2>
              <p className="text-xs text-[var(--color-mute)]">
                Feed the document into any generator (openapi-typescript, openapi-python-client,
                oapi-codegen) for a typed client.
              </p>
            </div>
          </div>
          <CodeSnippet title="OpenAPI URL" code={`curl ${specUrl} -o clickweave.openapi.json`} />
        </section>

        <section className="space-y-2 rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-5">
          <div className="flex items-start gap-3">
            <Key
              aria-hidden="true"
              size={24}
              weight="duotone"
              className="text-[var(--color-accent-brand)]"
            />
            <div className="flex-1">
              <h2 className="text-base font-semibold text-[var(--color-ink)]">Authentication</h2>
              <p className="text-sm text-[var(--color-mute)]">
                Every <span className="font-mono text-xs">/v1/*</span> request takes a bearer token
                — either your Clerk session JWT (used automatically from inside the dashboard) or an
                API key (<span className="font-mono text-xs">sk_live_…</span>) for server-to-server
                calls.
              </p>
              <Button variant="outline" size="sm" render={<Link to="/settings/keys" />}>
                Manage API keys →
              </Button>
            </div>
          </div>
        </section>
      </div>
    </Island>
  );
}
